import { useState, useEffect } from 'react';
import {
  getUsers,
  getInsuranceCompanies,
  getInsuranceAdjustors,
  getReferralSources,
} from '../api/leads-api';
import type {
  UserReference,
  InsuranceCompanyReference,
  InsuranceAdjustorReference,
  ReferralSourceReference,
} from '../types';

/**
 * Hook for fetching all reference data needed for lead forms.
 * Uses Promise.allSettled to ensure partial failures don't prevent other data from loading.
 */
export function useReferenceData() {
  const [users, setUsers] = useState<UserReference[]>([]);
  const [insuranceCompanies, setInsuranceCompanies] = useState<InsuranceCompanyReference[]>([]);
  const [insuranceAdjustors, setInsuranceAdjustors] = useState<InsuranceAdjustorReference[]>([]);
  const [referralSources, setReferralSources] = useState<ReferralSourceReference[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      const [usersResult, companiesResult, adjustorsResult, sourcesResult] = await Promise.allSettled([
        getUsers(),
        getInsuranceCompanies(),
        getInsuranceAdjustors(),
        getReferralSources(),
      ]);

      if (usersResult.status === 'fulfilled') {
        setUsers(usersResult.value);
      }
      if (companiesResult.status === 'fulfilled') {
        setInsuranceCompanies(companiesResult.value);
      }
      if (adjustorsResult.status === 'fulfilled') {
        setInsuranceAdjustors(adjustorsResult.value);
      }
      if (sourcesResult.status === 'fulfilled') {
        setReferralSources(sourcesResult.value);
      }

      // Set error if any critical fetch failed
      const failures = [usersResult, companiesResult, sourcesResult].filter(
        (r) => r.status === 'rejected'
      );
      if (failures.length > 0) {
        setError('Some reference data failed to load');
      }

      setIsLoading(false);
    };

    fetchData();
  }, []);

  return {
    users,
    insuranceCompanies,
    insuranceAdjustors,
    referralSources,
    isLoading,
    error,
  };
}
